import React from 'react';

import { VideoPreview } from '../../../components/VideoPreview';
import type { ChallengeStatus, ChallengeType } from '../types';
import { ChallengeInstruction } from './ChallengeInstruction';
import { ChallengeResult } from './ChallengeResult';
import { ChallengeTimer } from './ChallengeTimer';
import { FaceGuide } from './FaceGuide';

interface ChallengeOverlayProps {
  isActive: boolean;
  challengeType?: ChallengeType;
  progress: number;
  status?: ChallengeStatus;
  isWarmUp?: boolean;
  timeoutSeconds: number;
  stream?: MediaStream | null;
}

export const ChallengeOverlay: React.FC<ChallengeOverlayProps> = ({
  isActive,
  challengeType,
  progress,
  status,
  isWarmUp,
  timeoutSeconds,
  stream,
}) => {
  if (!isActive || !challengeType) {
    return null;
  }

  const isFinished =
    status === 'passed' || status === 'failed' || status === 'timeout';
  const isFaceChallenge = true;
  const isRunning = !isWarmUp && !isFinished;

  let liveBadgeLabel = 'Em andamento';
  if (isFinished) {
    liveBadgeLabel = 'Concluído';
  } else if (isWarmUp) {
    liveBadgeLabel = 'Preparação';
  }

  const renderStatusCopy = () => {
    if (isFinished) {
      return 'Desafio concluído. O monitoramento segue ativo em segundo plano.';
    }

    if (isWarmUp) {
      return 'Sua câmera está ativa. Posicione o rosto dentro do enquadramento e aguarde a instrução começar.';
    }

    return 'Siga a instrução na tela enquanto acompanha seu enquadramento ao vivo.';
  };

  const renderContent = () => {
    if (isFinished) {
      return <ChallengeResult status={status} />;
    }

    if (isWarmUp) {
      return (
        <>
          <div className="mb-3">
            <ChallengeInstruction type={challengeType} />
          </div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">
            Verificação de identidade
          </p>
          <p className="mt-2 text-sm text-white/75">
            Aguarde iniciar o movimento.
          </p>
        </>
      );
    }

    return (
      <>
        <div className="mb-4">
          <ChallengeInstruction type={challengeType} />
        </div>
        <div className="mb-4 rounded-2xl border border-white/10 bg-white/5 p-3">
          <p className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-300">
            Instrução atual
          </p>
          <p className="mt-1 text-sm text-white">
            {isFaceChallenge
              ? 'Siga a orientação e mantenha o rosto dentro do enquadramento.'
              : 'Siga a orientação com o corpo e mantenha o rosto visível.'}
          </p>
        </div>
        <div className="flex items-center justify-between gap-4">
          <ChallengeTimer timeoutSeconds={timeoutSeconds} />
          <p className="max-w-[170px] text-xs text-slate-300">
            Acompanhe o progresso e mantenha a movimentação suave.
          </p>
        </div>
      </>
    );
  };

  const renderMobileContent = () => {
    if (isFinished) {
      return <ChallengeResult status={status} />;
    }

    if (isWarmUp) {
      return (
        <>
          <ChallengeInstruction type={challengeType} variant="mobile" />
          <div className="mt-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-300">
              Preparação
            </p>
            <p className="mt-1 text-sm text-slate-100">
              Enquadre o rosto no topo da tela. O desafio começa em instantes.
            </p>
          </div>
        </>
      );
    }

    return (
      <>
        <ChallengeInstruction type={challengeType} variant="mobile" />
        <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-3">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">
            Como executar
          </p>
          <p className="mt-1.5 text-sm leading-6 text-white">
            Faça somente o movimento solicitado e mantenha os olhos no celular.
          </p>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5">
          <ChallengeTimer timeoutSeconds={timeoutSeconds} size={84} />
          <p className="max-w-[170px] text-xs leading-5 text-slate-300">
            Mova-se com suavidade. Quando concluir, volte ao centro da câmera.
          </p>
        </div>
      </>
    );
  };

  const renderMainPreview = () => {
    if (isFaceChallenge) {
      return <FaceGuide progress={progress} stream={stream} compact />;
    }

    if (stream) {
      return (
        <div className="relative w-full overflow-hidden rounded-[1.25rem] border border-white/10 bg-slate-950">
          <VideoPreview
            stream={stream}
            width="100%"
            height="100%"
            className="aspect-[4/5] border-0"
          />
        </div>
      );
    }

    return (
      <div className="flex aspect-[4/5] items-center justify-center rounded-[1.25rem] border border-dashed border-white/20 bg-slate-900 px-6 text-center text-sm text-slate-400">
        Aguardando imagem da webcam
      </div>
    );
  };

  const renderMiniPreview = () => {
    let cameraGuideColor = '#ef4444';
    if (progress >= 0.67) {
      cameraGuideColor = '#10b981';
    } else if (progress >= 0.33) {
      cameraGuideColor = '#f59e0b';
    }

    if (!stream) {
      return (
        <div className="flex h-full w-full items-center justify-center rounded-xl border border-dashed border-white/20 bg-slate-900 text-[10px] text-slate-400">
          Sem câmera
        </div>
      );
    }

    return (
      <div className="relative h-full w-full overflow-hidden rounded-xl border border-cyan-400/20 bg-slate-950">
        <VideoPreview
          stream={stream}
          width="100%"
          height="100%"
          className="h-full w-full border-0"
          videoClassName="scale-[1.06] object-cover"
        />
        <svg
          viewBox="0 0 100 130"
          className="pointer-events-none absolute inset-1 h-[calc(100%-0.5rem)] w-[calc(100%-0.5rem)]"
          preserveAspectRatio="xMidYMid meet"
        >
          <ellipse
            cx="50"
            cy="65"
            rx="30"
            ry="42"
            fill="none"
            stroke={cameraGuideColor}
            strokeWidth="2.5"
          />
        </svg>
      </div>
    );
  };

  return (
    <div className="pointer-events-auto fixed inset-0 z-50 overflow-y-auto bg-slate-950/75 px-2 py-2 sm:px-3 sm:py-3">
      <div className="relative mx-auto w-full max-w-4xl overflow-hidden rounded-2xl border border-white/10 bg-slate-950/95 p-2.5 text-white shadow-2xl backdrop-blur sm:rounded-[1.75rem] sm:p-4 lg:max-h-[95dvh] lg:p-5">
        <div className="pointer-events-none absolute -left-10 top-8 h-40 w-40 rounded-full bg-cyan-500/15 blur-3xl" />
        <div className="pointer-events-none absolute bottom-8 right-8 h-48 w-48 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="space-y-3 md:hidden">
          <div className="rounded-[1.4rem] border border-white/10 bg-slate-900/90 p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-cyan-300">
                    Câmera ao vivo
                  </p>
                  <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-semibold text-slate-200">
                    {liveBadgeLabel}
                  </span>
                </div>
                <p className="mt-2 text-sm leading-5 text-slate-200">
                  {isRunning
                    ? 'Acompanhe seu rosto na mini câmera e execute a instrução abaixo.'
                    : renderStatusCopy()}
                </p>
              </div>
              <div className="h-24 w-20 shrink-0 sm:h-28 sm:w-24">
                {renderMiniPreview()}
              </div>
            </div>
          </div>

          <div className="rounded-[1.4rem] border border-white/10 bg-slate-900/70 p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-amber-300/20 bg-amber-300/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-200">
                Verificação de identidade
              </span>
              <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-slate-200">
                Janela rápida durante a prova
              </span>
            </div>
            <div className="mt-3">{renderMobileContent()}</div>
            <div className="mt-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-[11px] leading-5 text-slate-300">
              <span className="font-semibold text-white">Dica:</span> leia a
              instrução, execute uma ação por vez e mantenha o rosto visível.
            </div>
          </div>
        </div>

        <div className="hidden min-h-0 gap-4 md:grid md:grid-cols-[minmax(0,1.15fr),minmax(0,0.85fr)]">
          <div className="flex min-h-0 flex-col rounded-[1.5rem] border border-white/10 bg-slate-900/90 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-cyan-300">
                Câmera ao vivo
              </p>
              <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-slate-200">
                {liveBadgeLabel}
              </span>
            </div>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              {renderStatusCopy()}
            </p>
            <div className="mt-4">
              <div
                className={`${
                  isRunning ? 'shadow-[0_0_0_4px_rgba(34,211,238,0.12)]' : ''
                }`}
              >
                {renderMainPreview()}
              </div>
            </div>
            <div className="mt-3 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-2 text-sm text-cyan-100">
              Mantenha seu rosto visível, bem iluminado e alinhado.
            </div>
          </div>

          <div className="flex min-h-0 flex-col justify-between rounded-[1.5rem] border border-white/10 bg-slate-900/70 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-amber-300/20 bg-amber-300/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-200">
                  Verificação de identidade
                </span>
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold text-slate-200">
                  Janela rápida durante a prova
                </span>
              </div>
              <h2 className="mt-3 text-2xl font-semibold text-white">
                Confirme sua presença na câmera
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-300">
                O quadro ao vivo valida o movimento atual com baixa carga
                visual.
              </p>
            </div>

            <div className="mt-4 flex flex-1 flex-col justify-center">
              {renderContent()}
            </div>

            <div className="mt-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs leading-5 text-slate-300">
              <span className="font-semibold text-white">Dica:</span> não saia
              do enquadramento e mantenha a cabeça estável após concluir o
              movimento.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
